import { useState } from 'react'
import { Base } from '@itsrever/design-system'
import { useTranslation } from 'react-i18next'
import {
  INSTALLED_PLATFORMS,
  PICKER_PLATFORMS,
  PickerPlatform
} from '@/domain/nango-connection'
import { ProviderLogo, providerName, providerOf, RAW_TEXT } from './hub-parts'

const {
  Button,
  DialogRoot,
  DialogPortal,
  DialogBackdrop,
  DialogPopup,
  DialogTitle,
  DialogDescription,
  DialogCloseButton,
  DialogHeader,
  DialogFooter
} = Base

const nameOf = (integration: string) =>
  providerName({ integration, provider: integration })

const PickerOption = ({
  integration,
  note,
  disabled,
  onClick
}: {
  integration: string
  note?: string
  disabled?: boolean
  onClick: () => void
}) => (
  <Button
    type="button"
    variant="secondary"
    className="h-auto justify-start gap-3 px-4 py-3 text-left"
    disabled={disabled}
    onClick={onClick}
  >
    <ProviderLogo
      provider={providerOf({ integration, provider: integration })}
      name={nameOf(integration)}
    />
    <span className="flex flex-col gap-0.5">
      {nameOf(integration)}
      {note ? (
        <span className="text-xs font-normal text-neutral-600">{note}</span>
      ) : null}
    </span>
  </Button>
)

// Ours, not Nango's list: Nango draws integrations on a generic provider with a plug icon.
export const PlatformPickerDialog = ({
  open,
  onClose,
  onPick
}: {
  open: boolean
  onClose: () => void
  onPick: (integration: string) => void
}) => {
  const { t } = useTranslation()
  // Set once the merchant picks a platform that several integrations connect.
  const [platform, setPlatform] = useState<PickerPlatform>()
  if (!open) return null

  const close = () => {
    setPlatform(undefined)
    onClose()
  }
  const pick = (integration: string) => {
    setPlatform(undefined)
    onPick(integration)
  }

  return (
    <DialogRoot
      open
      onOpenChange={(isOpen) => {
        if (!isOpen) close()
      }}
    >
      <DialogPortal>
        <DialogBackdrop />
        {/* On a short or zoomed screen the popup scrolls instead of leaving the screen. */}
        <DialogPopup
          size="sm"
          className="max-h-[calc(100vh-2rem)] overflow-y-auto"
        >
          <div className="flex flex-col gap-4">
            <DialogCloseButton />
            <DialogHeader>
              <DialogTitle>
                {platform
                  ? t('integrations_hub.platform.pick_integration_title', {
                      name: nameOf(platform.platform),
                      ...RAW_TEXT
                    })
                  : t('integrations_hub.platform.pick_title')}
              </DialogTitle>
              <DialogDescription>
                {platform
                  ? t('integrations_hub.platform.pick_integration_description')
                  : t('integrations_hub.platform.pick_description')}
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-2">
              {platform
                ? platform.integrations.map((integration) => (
                    <PickerOption
                      key={integration}
                      integration={integration}
                      onClick={() => pick(integration)}
                    />
                  ))
                : PICKER_PLATFORMS.map((option) => {
                    const installed = INSTALLED_PLATFORMS.includes(
                      option.platform
                    )
                    return (
                      <PickerOption
                        key={option.platform}
                        integration={option.platform}
                        disabled={installed}
                        note={
                          installed
                            ? t('integrations_hub.platform.installed_note', {
                                name: nameOf(option.platform),
                                ...RAW_TEXT
                              })
                            : undefined
                        }
                        onClick={() =>
                          option.integrations.length === 1
                            ? pick(option.integrations[0])
                            : setPlatform(option)
                        }
                      />
                    )
                  })}
            </div>
            {platform ? (
              <DialogFooter className="justify-start">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setPlatform(undefined)}
                >
                  {t('integrations_hub.platform.pick_back')}
                </Button>
              </DialogFooter>
            ) : null}
          </div>
        </DialogPopup>
      </DialogPortal>
    </DialogRoot>
  )
}
